import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';
import { AxiosInstance } from '../../../../../utilities/requestClient';
import RaptorDatePicker from '../../../../selects/RaptorDatePicker.component';
import { toast } from '../../../../toast/Toast';
import GenericDropdown from '../../../../ui/GenericDropDown';
import LoadingButton from '../../../../ui/LoadingButton';
import dayjs from 'dayjs';
import type { RaptorExportUrl, RaptorExportFund } from '../GeneralRaptorExport';

/**
 * Applies optional path params in templates like:
 *   "generate_liquidty_report/<fund_id>/<position_date>"
 *
 * - If a value is provided, the placeholder is replaced.
 * - If no value is provided, the entire "/<placeholder>" segment is removed.
 */
function buildOptionalPath(
  template: string,
  params: { fund_name?: string; position_date?: string | null },
): string {
  let path = template;

  if (params.fund_name) {
    path = path.replace('<fund_name>', encodeURIComponent(params.fund_name));
  } else {
    path = path.replace(/\/?<fund_name>/, '');
  }

  if (params.position_date) {
    path = path.replace(
      '<position_date>',
      encodeURIComponent(params.position_date),
    );
  } else {
    path = path.replace(/\/?<position_date>/, '');
  }

  // Clean up any leftover "<...>" segments just in case
  return path.replace(/\/?<[^>]+>/g, '');
}

function getExtensionFromMime(mime: string) {
  const map: Record<string, string> = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "text/csv": "csv",
    "application/json": "json",
    "application/zip": "zip",
  };

  return map[mime] || "bin";
}


type FundDateExportItemProps = {
  item: RaptorExportUrl;
  funds?: RaptorExportFund[] | null;
};

const FundDateExportItem: React.FC<FundDateExportItemProps> = ({
  item,
  funds,
}) => {
  const safeLabel = item.name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const [selectedFund, setSelectedFund] = useState<RaptorExportFund | null>(null);
  const [availableFundDates, setAvailableFundDates] = useState<string[]>([]);
  const [selectedFundPositionDate, setSelectedFundPositionDate] = useState<
    string | null
  >(null);

  const fundDateDownloadMutation = useMutation({
    mutationFn: async ({ url }: { url: string }) => {
      const response = await AxiosInstance.get(url, {
        responseType: 'blob',
      });
      return { blob: response.data as Blob };
    },
  });

  // Helper function to append raptorPlatformId to download URLs if it exists
  const getDownloadUrl = (baseUrl: string) => {
    if (raptorPlatformId) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}raptor_platform_id=${raptorPlatformId}`;
    }
    return baseUrl;
  };

  // When fund selection changes, use position_dates from the link object (fund)
  useEffect(() => {
    if (!selectedFund) {
      setAvailableFundDates([]);
      setSelectedFundPositionDate(null);
      return;
    }

    const fundDates = selectedFund.position_dates ?? [];
    setAvailableFundDates(fundDates);

    // Keep previous selection if still valid, otherwise default to first available date
    if (fundDates.length) {
      setSelectedFundPositionDate((prev) =>
        prev && fundDates.includes(prev) ? prev : fundDates[0],
      );
    } else {
      setSelectedFundPositionDate(null);
    }
  }, [selectedFund]);


  const hasFunds = funds && funds.length > 0;
  const hasDates =
    availableFundDates.length > 0 && selectedFundPositionDate;

  const downloadPath = buildOptionalPath(item.url, {
    fund_name: selectedFund?.fund_name,
    position_date: selectedFundPositionDate,
  });


  const handleFundDateDownload = () => {
    if (!selectedFund || !selectedFundPositionDate) {
      return;
    }
    const urlWithPlatform = getDownloadUrl(downloadPath);
    const baseName = `${safeLabel}-${selectedFund.fund_name}-${selectedFundPositionDate}`;

    fundDateDownloadMutation.mutate(
      { url: urlWithPlatform },
      {
        onSuccess: ({ blob }) => {
          const ext = getExtensionFromMime(blob.type);
          const fileName = `${baseName}.${ext}`;
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
        },
        onError: (err) => {
          toast({
            title: 'Download failed',
            message:
              err instanceof Error ? err.message : 'Export failed. Please try again.',
            type: 'error',
            duration: 5000,
          });
        },
      },
    );
  };

  return (
    <li>
      <div className="flex flex-col gap-2 rounded border border-gray-100 bg-white p-3 shadow-sm">
        <div className="text-[1.10rem] font-semibold text-[#003B71]">
          {`${item.name} (.${item.file_type})`}
        </div>
        {item.description && (
          <div className="text-sm text-[#003B71]">{item.description}</div>
        )}

        {hasFunds ? (
          <>
            <GenericDropdown
              data={funds!}
              selected={selectedFund}
              setSelected={(value) => setSelectedFund(value as RaptorExportFund)}
              getDisplayValue={(f: RaptorExportFund) => f.fund_name_full || f.fund_name}
              label={
                selectedFund
                  ? selectedFund.fund_name_full || selectedFund.fund_name
                  : 'Select fund'
              }
              tooltip="Pick a fund for this export"
              searchBar
              className="w-96"
              dropdownClassName="w-96"
            />

            {selectedFund && availableFundDates.length > 0 && (
              <RaptorDatePicker
                title="Position date"
                handler={(val) => {
                  setSelectedFundPositionDate(
                    dayjs(val).format('YYYY-MM-DD'),
                  );
                }}
                datesToInclude={availableFundDates}
                selectedDate={dayjs(
                  selectedFundPositionDate || availableFundDates[0],
                )}
                tiedToActiveDate={false}
              />
            )}

            <LoadingButton
              type="button"
              onClick={handleFundDateDownload}
              isLoading={fundDateDownloadMutation.isPending}
              disabled={!hasDates}
              className="px-4 py-1.5 w-96 text-sm font-medium hover:opacity-90 focus:opacity-90"
            >
              Download
            </LoadingButton>


          </>
        ) : (
          <p className="text-xs text-gray-500">
            No funds available for this export.
          </p>
        )}
      </div>
    </li>
  );
};

export default FundDateExportItem;

